import { Button, Group } from '@mantine/core';
import { IconPencil, IconPlus, IconTrash } from '@tabler/icons-react';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { RowActions } from 'src/Components/Shared/RowActions';

import { RoomCard } from '../RoomCard';

import styles from './FloorBand.module.scss';
import { Props } from './FloorBand.types';

// Explicit level text alongside the free-form name so a RDC accidentally saved at +1
// stands out (prod incident 09/26, Ferme des Ébihens).
const formatLevel = (level: number, t: TFunction): string => {
  if (level === 0) return t('admin.structure.floors.levelLabel.ground', 'Rez-de-chaussée');
  if (level === 1) return t('admin.structure.floors.levelLabel.firstFloor', '1er étage');
  if (level > 1)
    return t('admin.structure.floors.levelLabel.nthFloor', {
      n: level,
      defaultValue: '{{n}}e étage',
    });
  if (level === -1) return t('admin.structure.floors.levelLabel.basement', 'Sous-sol');
  return t('admin.structure.floors.levelLabel.basementN', {
    n: -level,
    defaultValue: 'Sous-sol -{{n}}',
  });
};

const FloorBand = ({
  floor,
  rooms,
  showHeader,
  onAddRoom,
  onEditFloor,
  onDeleteFloor,
  onOpenRoom,
  onEditRoom,
  onToggleOutOfService,
  onDeleteRoom,
  enSuiteLabelOf,
}: Props) => {
  const { t } = useTranslation();
  const roomCount = rooms.filter(room => !room.isOutOfService).length;

  return (
    <div className={styles.band}>
      {showHeader && (
        <div className={styles.header}>
          {floor && <span className={styles.level}>{formatLevel(floor.level, t)}</span>}
          <span className={styles.name}>
            {floor?.name ?? t('admin.structure.floors.noFloor', 'Sans niveau')}
          </span>
          <span className={styles.count}>
            {t('admin.structure.rooms.count', {
              count: roomCount,
              defaultValue: '{{count}} pièce',
              defaultValue_other: '{{count}} pièces',
            })}
          </span>
          <span className={styles.spacer} />
          <Button
            variant="outline"
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={() => onAddRoom(floor?.id ?? null)}
          >
            {t('admin.structure.rooms.add', 'Ajouter une pièce')}
          </Button>
          {floor && (onEditFloor ?? onDeleteFloor) && (
            <Group gap="xs">
              <RowActions
                actions={[
                  ...(onEditFloor
                    ? [
                        {
                          key: 'edit',
                          label: t('common.edit', 'Modifier'),
                          icon: <IconPencil size={18} />,
                          onClick: () => onEditFloor(floor),
                        },
                      ]
                    : []),
                  ...(onDeleteFloor
                    ? [
                        {
                          key: 'delete',
                          label: t('common.delete', 'Supprimer'),
                          icon: <IconTrash size={18} />,
                          variant: 'danger' as const,
                          onClick: () => onDeleteFloor(floor),
                        },
                      ]
                    : []),
                ]}
              />
            </Group>
          )}
        </div>
      )}

      {rooms.length === 0 ? (
        <div className={styles.empty}>
          {t('admin.structure.rooms.emptyFloor', 'Aucune pièce sur ce niveau pour l’instant.')}
        </div>
      ) : (
        <div className={styles.roomList}>
          {rooms.map(room => (
            <RoomCard
              key={room.id}
              room={room}
              servedRoomName={enSuiteLabelOf(room)}
              onOpen={onOpenRoom}
              onEdit={onEditRoom}
              onToggleOutOfService={onToggleOutOfService}
              onDelete={onDeleteRoom}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default FloorBand;
